import React from 'react';
import { NavLink } from 'react-router-dom';
import { Row, Col, Form } from 'react-bootstrap';
import PropTypes from "prop-types";
import { connect } from "react-redux";

import CsLineIcons from '../../cs-line-icons';
import { sendReset as reset } from "../../actions/auth";
import { send as sendAlert } from '../../actions/alert';
import { settings as pageSettings } from '../../actions/page';
import { isEmail } from '../../utils/regex';

import LayoutFullPage from '../../layout/FullPage';
import Header from '../../layout/header';
import ConditionalButton from '../../layout/conditional-button';

export class Forgot extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            email: this.email || "",
            submitting: false,
            feedback: "",
            feedbackType: ""
        };
    }

    async componentDidMount() {
        await this.props.pageSettings();
    }

    onChange = (e) => {
        this.setState({ [e.target.name]: e.target.value });
    }

    onSubmit = async (e) => {
        if (e?.preventDefault) e.preventDefault();
        if (!isEmail(this.state.email)) {
            this.props.sendAlert("Please enter a valid email.", "error");
            return;
        }
        
        this.setState({
            submitting: true,
            feedback: "Sending password reset email…",
            feedbackType: "pending"
        });

        const result = await this.props.reset({
            instance: this.props?.page?.uuid,
            email: this.state.email,
        });

        if (result?.success) {
            const message = "Password reset email sent. Check your inbox and spam folder.";
            this.props.sendAlert(message, "success");
            this.setState({
                submitting: false,
                feedback: message,
                feedbackType: "success"
            });
            return;
        }

        const message = result?.message || "We couldn't send a reset email. Confirm the address and try again.";
        this.props.sendAlert(message, "error");
        this.setState({
            submitting: false,
            feedback: message,
            feedbackType: "error"
        });
    };

    line(index) {
        return (this.props?.page?.meta?.lines?.length > index) ? this.props.page.meta.lines[index] : "";
    }

    get email() {
        return new URLSearchParams(window.location.search).get("e") || undefined;
    }

    get leftSide() {
        return (
            <div className="min-h-100 d-flex align-items-center">
                <div className="w-100 w-lg-75 w-xxl-50">
                    <div>
                        <div className="mb-5">
                            <h1 className="display-3 text-white">{this.line(2)}</h1>
                        </div>
                        <p className="h6 text-white lh-1-5 mb-5" dangerouslySetInnerHTML={{ __html: this.line(3) }} />
                        <div className="mb-5"></div>
                    </div>
                </div>
            </div>
        );
    }

    get rightSide() {
        const canProceed = isEmail(this.state.email) && !this.state.submitting;

        return (
            <div className="sw-lg-70 min-h-100 bg-foreground d-flex justify-content-center align-items-center shadow-deep py-5 full-page-content-right-border">
                <div className="sw-lg-50 px-5">
                    <div className="sh-11">
                        <NavLink to="/">
                            <div className="logo-default" style={{ backgroundImage: `url(${this.props.page?.meta?.images?.logos?.small})` }} />
                        </NavLink>
                    </div>

                    <div className="mb-5">
                        <h2 className="cta-1 mb-0 text-primary">Password trouble?</h2>
                        <h2 className="cta-1 text-secondary" style={{ fontSize: ".8rem" }}>Let's change it now!</h2>
                    </div>

                    <div className="mb-5">
                        <p className="h6">
                            Please enter the email address that is associated with your account. If found, we will send you a link to change your password.
                        </p>
                    </div>

                    <div>
                        <form onSubmit={this.onSubmit}>
                            <Row>
                                <Col sm="12">
                                    <div className="mb-3 filled form-group tooltip-end-top">
                                        <CsLineIcons icon="email" />
                                        <Form.Control
                                            className="form-control"
                                            name="email"
                                            value={this.state.email}
                                            onChange={this.onChange}
                                            placeholder='Email'
                                            required
                                        />
                                    </div>
                                </Col>

                                <Col sm="6" style={{ textAlign: "right" }}>
                                    <ConditionalButton
                                        enabledVariant="outline-primary"
                                        tooltip="Send reset email"
                                        display={true}
                                        enabled={canProceed}
                                        onEnabledClick={this.onSubmit}
                                    >
                                        {this.state.submitting ? "Sending…" : "Send Reset Email"}
                                    </ConditionalButton>
                                </Col>
                                <Col sm="12">
                                    <div
                                        className={`mt-3 small ${
                                            this.state.feedbackType === "error"
                                                ? "text-danger"
                                                : this.state.feedbackType === "success"
                                                    ? "text-success"
                                                    : "text-muted"
                                        }`}
                                        role={this.state.feedbackType === "error" ? "alert" : "status"}
                                        aria-live={this.state.feedbackType === "error" ? "assertive" : "polite"}
                                        aria-atomic="true"
                                    >
                                        {this.state.feedback}
                                    </div>
                                </Col>
                            </Row>
                        </form>
                    </div>
                </div>
            </div>
        );
    };

    render() {
        return (
            <>
                <Header
                    title='Forgot'
                    description='Use this page if you forgot your password. It will send the password to your email, if it is found.'
                />
                <LayoutFullPage left={this.leftSide} right={this.rightSide} background={this.props.page?.meta?.images?.background}  />
            </>
        );
    }
};

Forgot.propTypes = {
    reset: PropTypes.func.isRequired,
    sendAlert: PropTypes.func.isRequired,
    pageSettings: PropTypes.func.isRequired,
    page: PropTypes.object
};

const mapStateToProps = (state) => {
    return ({
        page: state.page
    });
};

export default connect(mapStateToProps, { pageSettings, reset, sendAlert })(Forgot);
